import React from "react";
import { BrowserRouter, Routes, Route, NavLink, Link } from "react-router-dom";

const CONTACT_EMAILS = {
  quotes: "quotes@ewmtransport.com",
  southampton: "southampton@ewmtransport.com",
  felixstowe: "felixstowe@ewmtransport.com",
  london: "london@ewmtransport.com",
  compliance: "compliance@ewmtransport.com",
};

const BRAND = {
  navy: "#0f2231",
  lightBlue: "#a7c4df",
  coral: "#ef8e7d",
  offWhite: "#f7fafc",
  slate: "#0c1a25",
};

const LOGO = "/images/ewm logo.png";
const RAW_IMAGES = [
  "/images/1730224412324.jpeg",
  "/images/1730224412526.jpeg",
  "/images/1741364578747.jpeg",
  "/images/1741852018469.jpeg",
  "/images/1741852020195.jpeg",
  "/images/WhatsApp Image 2025-10-20 at 13.52.20_1a55a474.jpg",
];
const IMAGES = RAW_IMAGES.map((p) => encodeURI(p));

function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: BRAND.offWhite }}>
      <TopBar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function TopBar() {
  const linkBase = "px-4 py-2 rounded-xl text-sm font-medium transition-all";
  const linkActive = { backgroundColor: BRAND.coral, color: "white" };
  return (
    <header className="sticky top-0 z-50 bg-white/90 border-b" style={{ borderColor: BRAND.lightBlue }}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex justify-between items-center h-16">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO} alt="EWM Transport Ltd" className="w-12 h-12 object-contain rounded-xl ring-1" style={{ background: BRAND.navy, ringColor: BRAND.lightBlue }} />
          <div className="leading-tight">
            <div className="text-xl font-extrabold" style={{ color: BRAND.lightBlue }}>EWM Transport Ltd</div>
            <div className="text-xs uppercase tracking-wider" style={{ color: BRAND.coral }}>Container Haulage Specialists</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-2">
          <NavLink to="/" end className={() => linkBase} style={({ isActive }) => (isActive ? linkActive : { color: BRAND.slate })}>Home</NavLink>
          <NavLink to="/about" className={() => linkBase} style={({ isActive }) => (isActive ? linkActive : { color: BRAND.slate })}>About Us</NavLink>
          <NavLink to="/what-we-do" className={() => linkBase} style={({ isActive }) => (isActive ? linkActive : { color: BRAND.slate })}>What We Do</NavLink>
          <NavLink to="/subcontractors" className={() => linkBase} style={({ isActive }) => (isActive ? linkActive : { color: BRAND.slate })}>Subcontractors</NavLink>
          <NavLink to="/contact" className={() => linkBase} style={({ isActive }) => (isActive ? linkActive : { color: BRAND.slate })}>Contact Us</NavLink>
        </nav>
      </div>
    </header>
  );
}

function PageBanner({ src }) {
  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="aspect-[4/2] w-full ring-1 overflow-hidden rounded-3xl bg-white" style={{ ringColor: BRAND.lightBlue }}>
          <img src={src} alt="EWM trucks" className="h-full w-full object-cover" loading="eager" />
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${BRAND.navy}, #132d42)` }} />
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-28 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <h1 className="text-5xl font-extrabold mb-4" style={{ color: BRAND.lightBlue }}>One of the UK's Fastest Growing Container Haulage Companies</h1>
          <p className="text-lg text-white/90">With <strong>40 own vehicles</strong> alongside <strong>60+ dedicated subcontractors</strong> and depots in <strong>Southampton</strong>, <strong>London Gateway</strong>, and <strong>Felixstowe</strong>, our coverage means we service all major ports in the UK.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/what-we-do" className="px-5 py-3 rounded-2xl font-semibold" style={{ background: BRAND.coral, color: "white" }}>What We Do</Link>
            <Link to="/about" className="px-5 py-3 rounded-2xl font-semibold ring-1" style={{ color: BRAND.lightBlue, ringColor: BRAND.lightBlue }}>About EWM</Link>
          </div>
        </div>
        <div className="aspect-[4/3] w-full rounded-3xl shadow-2xl ring-1 overflow-hidden flex items-center justify-center bg-white" style={{ ringColor: BRAND.lightBlue }}>
          <img src="/images/ewm%20logo.png" alt="EWM Transport Ltd" className="max-h-full max-w-full object-contain p-6" loading="eager" />
        </div>
      </div>
    </section>
  );
}

function Home() {
  return (
    <>
      <Hero />
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Nationwide Coverage", text: "Serving all major UK ports from Southampton, London Gateway & Felixstowe." },
              { title: "Trusted Fleet", text: "40 own vehicles + 60+ dedicated subcontractors." },
              { title: "Specialist Handling", text: "Hazardous, OOG & overweight container expertise." },
              { title: "Intermodal", text: "Integrated road, rail & sea solutions." },
            ].map((c) => (
              <div key={c.title} className="rounded-2xl p-5 ring-1 bg-white shadow-sm" style={{ ringColor: BRAND.lightBlue }}>
                <div className="text-sm font-semibold uppercase tracking-wider" style={{ color: BRAND.coral }}>{c.title}</div>
                <p className="mt-2 text-gray-700">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function About() {
  return (
    <section className="pb-16">
      <PageBanner src={IMAGES[0]} />
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <h2 className="text-4xl font-extrabold mb-6" style={{ color: BRAND.navy }}>About Us</h2>
        <p className="text-lg text-gray-800 mb-4">Founded in <strong>2019</strong>, EWM Transport Ltd has grown year on year thanks to an unwavering commitment to excellence. From day one, our focus has been on reliability, safety, and customer service.</p>
        <p className="text-lg text-gray-800">We strive to be the <strong>leading provider of transportation services</strong>, delivering solutions that exceed expectations. Our team, fleet and network of dedicated subcontractors enable us to provide flexible, responsive and high-quality container logistics across the UK.</p>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {[
            { k: "Est.", v: "2019" },
            { k: "Own Vehicles", v: "40" },
            { k: "Dedicated Subcontractors", v: "60+" },
          ].map(({ k, v }) => (
            <div key={k} className="rounded-2xl p-5 bg-white ring-1 text-center" style={{ ringColor: BRAND.lightBlue }}>
              <div className="text-sm uppercase tracking-wider" style={{ color: BRAND.coral }}>{k}</div>
              <div className="mt-2 text-3xl font-extrabold" style={{ color: BRAND.navy }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatWeDo() {
  return (
    <section className="pb-16">
      <PageBanner src={IMAGES[1]} />
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <h2 className="text-4xl font-extrabold mb-8" style={{ color: BRAND.navy }}>What We Do</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { title: "Nationwide UK Container Haulage", text: "Reliable port-to-door and door-to-port services across the UK with live tracking and proactive communication." },
            { title: "Hazardous Container Haulage", text: "Trained drivers and compliant equipment to handle ADR/hazardous cargo safely and efficiently." },
            { title: "Out of Gauge & Overweight Specialists", text: "Expertise and permits for complex, oversized and heavy containers including route planning and escorts when required." },
            { title: "Intermodal Solutions", text: "Benefit from our seamless intermodal solutions that optimise the use of multiple modes of transportation, including road, rail, and sea for cost-effective and environmentally friendly transport." },
          ].map((s) => (
            <div key={s.title} className="rounded-2xl p-6 bg-white ring-1 shadow-sm" style={{ ringColor: BRAND.lightBlue }}>
              <div className="text-base font-semibold uppercase tracking-wide" style={{ color: BRAND.coral }}>{s.title}</div>
              <div className="mt-2 text-gray-800">{s.text}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Subcontractors() {
  return (
    <section className="pb-16">
      <PageBanner src={IMAGES[2]} />
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <h2 className="text-4xl font-extrabold mb-4" style={{ color: BRAND.navy }}>Dedicated Subcontractors</h2>
        <p className="text-lg mb-6 text-gray-800">Do you want to join the EWM Team as a dedicated subcontractor? We offer consistent work, fair terms, and a professional operation that values long-term partnerships.</p>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            "Weekly Payment Terms",
            "Guaranteed Work 52 Weeks Of The Year",
            "Work Available From All Areas Of The Country",
            "Day, Night & Tramping Work",
            "Trailer Rental",
            "Fuel Available At Cost Price",
          ].map((item) => (
            <div key={item} className="rounded-2xl p-5 bg-white ring-1" style={{ ringColor: BRAND.lightBlue }}>
              <div className="flex items-start gap-3">
                <div className="mt-1 w-2.5 h-2.5 rounded-full" style={{ background: BRAND.coral }} />
                <p className="text-gray-800">{item}</p>
              </div>
            </div>
          ))}
        </div>
        <div id="apply" className="mt-12 p-6 bg-white rounded-2xl ring-1" style={{ ringColor: BRAND.lightBlue }}>
          <div className="text-base font-semibold uppercase tracking-wide" style={{ color: BRAND.coral }}>Register Your Interest</div>
          <p className="text-gray-700 mt-1">Share your details and our team will get back to you.</p>
          <form className="mt-6 grid md:grid-cols-2 gap-4" action="/subcontractor.php" method="POST">
            <input type="text" name="website" style={{display:"none"}} tabIndex="-1" autoComplete="off" />
            <input name="name" className="px-4 py-3 rounded-xl ring-1 focus:outline-none" placeholder="Company / Driver Name" style={{ ringColor: BRAND.lightBlue }} required />
            <input name="email" type="email" className="px-4 py-3 rounded-xl ring-1 focus:outline-none" placeholder="Email" style={{ ringColor: BRAND.lightBlue }} required />
            <input name="phone" className="px-4 py-3 rounded-xl ring-1 focus:outline-none" placeholder="Phone" style={{ ringColor: BRAND.lightBlue }} />
            <input name="area" className="px-4 py-3 rounded-xl ring-1 focus:outline-none" placeholder="Operating Area (e.g. Midlands)" style={{ ringColor: BRAND.lightBlue }} />
            <textarea name="details" className="md:col-span-2 px-4 py-3 rounded-xl ring-1 focus:outline-none" rows={4} placeholder="Fleet details (trucks, ADR, OOG capability, etc.)" style={{ ringColor: BRAND.lightBlue }} />
            <button className="md:col-span-2 px-5 py-3 rounded-2xl font-semibold" type="submit" style={{ background: BRAND.coral, color: "white" }}>Submit</button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section className="pb-16">
      <PageBanner src={IMAGES[3]} />
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <h2 className="text-4xl font-extrabold mb-6" style={{ color: BRAND.navy }}>Contact Us</h2>
        <p className="text-lg mb-4">We’d love to hear from you. Reach out to the relevant department below.</p>
        <ul className="space-y-4 text-lg text-gray-800">
          <li><strong>Telephone:</strong> <a href="tel:01702908531" className="text-blue-600 hover:underline">01702 908 531</a></li>
          <li><strong>Head Office:</strong> EWM Transport Ltd, 1035-1037 London Road, Leigh-On-Sea, SS9 3JY</li>
          <li><strong>Rates & General Enquiries:</strong> <a href={`mailto:${CONTACT_EMAILS.quotes}`} className="text-blue-600 hover:underline">{CONTACT_EMAILS.quotes}</a></li>
          <li><strong>Southampton:</strong> <a href={`mailto:${CONTACT_EMAILS.southampton}`} className="text-blue-600 hover:underline">{CONTACT_EMAILS.southampton}</a></li>
          <li><strong>Felixstowe:</strong> <a href={`mailto:${CONTACT_EMAILS.felixstowe}`} className="text-blue-600 hover:underline">{CONTACT_EMAILS.felixstowe}</a></li>
          <li><strong>London Gateway / Tilbury:</strong> <a href={`mailto:${CONTACT_EMAILS.london}`} className="text-blue-600 hover:underline">{CONTACT_EMAILS.london}</a></li>
          <li><strong>Fleet Management:</strong> <a href={`mailto:${CONTACT_EMAILS.compliance}`} className="text-blue-600 hover:underline">{CONTACT_EMAILS.compliance}</a></li>
        </ul>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="mt-20">
      <div className="pt-10 pb-12" style={{ background: BRAND.navy }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid md:grid-cols-3 gap-8 items-start">
          <div>
            <div className="text-lg font-extrabold" style={{ color: BRAND.lightBlue }}>EWM Transport Ltd</div>
            <p className="mt-2 text-white/80 text-sm max-w-sm">
              Container haulage across the UK. Depots in Southampton, London Gateway, and Felixstowe.
            </p>
          </div>
          <div>
            <div className="text-sm font-semibold uppercase tracking-wide text-white/70">Quick Links</div>
            <ul className="mt-2 space-y-2 text-white/80">
              <li><Link to="/about" className="hover:underline">About</Link></li>
              <li><Link to="/what-we-do" className="hover:underline">What We Do</Link></li>
              <li><Link to="/subcontractors" className="hover:underline">Subcontractors</Link></li>
              <li><Link to="/contact" className="hover:underline">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-sm font-semibold uppercase tracking-wide text-white/70">Contact</div>
            <ul className="mt-2 space-y-2 text-white/80 text-sm">
              <li>Email: <a className="hover:underline" href={`mailto:${CONTACT_EMAILS.quotes}`}>{CONTACT_EMAILS.quotes}</a></li>
              <li>Phone: <a className="hover:underline" href="tel:01702908531">01702 908 531</a></li>
              <li>Registered in England & Wales</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="py-4 text-center text-xs" style={{ background: BRAND.slate, color: "#9fb6c9" }}>
        © {new Date().getFullYear()} EWM Transport Ltd. All rights reserved.
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Shell>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/what-we-do" element={<WhatWeDo />} />
          <Route path="/subcontractors" element={<Subcontractors />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
      </Shell>
    </BrowserRouter>
  );
}
